import React, { useCallback, useState, useRef } from 'react';
import { Block, TableBlockData, TableCell } from '../../../config';
import LiveText from '../../LiveText';
import './table-block.scss';

interface TableBlockProps {
  block: Block;
  onChange: (data: TableBlockData) => void;
  onDelete: () => void;
  isEditing: boolean;
}

const TableBlock: React.FC<TableBlockProps> = ({ block, onChange, isEditing }) => {
  const data = block.data as TableBlockData;
  const tableRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [resizing, setResizing] = useState<{ colIndex: number; startX: number; startWidths: number[] } | null>(null);

  const updateCell = useCallback((rowIndex: number, colIndex: number, content: string) => {
    const newRows = [...data.rows];
    newRows[rowIndex] = [...newRows[rowIndex]];
    newRows[rowIndex][colIndex] = { ...newRows[rowIndex][colIndex], content };
    onChange({ ...data, rows: newRows });
  }, [data, onChange]);

  const addRow = useCallback(() => {
    const colCount = data.rows[0]?.length || 2;
    const newRow: TableCell[] = Array(colCount).fill(null).map(() => ({ content: '' }));
    onChange({ ...data, rows: [...data.rows, newRow] });
  }, [data, onChange]);

  const addColumn = useCallback(() => {
    const newRows = data.rows.map((row, index) => [
      ...row,
      { content: '', header: index === 0 && data.hasHeaderRow }
    ]);
    // Add width for new column (redistribute evenly)
    const colCount = newRows[0]?.length || 1;
    const newWidths = Array(colCount).fill(100 / colCount);
    onChange({ ...data, rows: newRows, columnWidths: newWidths });
  }, [data, onChange]);

  const deleteRow = useCallback(() => {
    if (data.rows.length <= 1) return;
    const newRows = data.rows.slice(0, -1);
    onChange({ ...data, rows: newRows });
  }, [data, onChange]);

  const deleteColumn = useCallback(() => {
    if (data.rows[0]?.length <= 1) return;
    const newRows = data.rows.map(row => row.slice(0, -1));
    // Remove last column width
    const newWidths = data.columnWidths?.slice(0, -1);
    onChange({ ...data, rows: newRows, columnWidths: newWidths });
  }, [data, onChange]);

  const toggleHeaderRow = useCallback(() => {
    const newRows = [...data.rows];
    if (newRows.length > 0) {
      newRows[0] = newRows[0].map(cell => ({
        ...cell,
        header: !data.hasHeaderRow
      }));
    }
    onChange({ ...data, rows: newRows, hasHeaderRow: !data.hasHeaderRow });
  }, [data, onChange]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (tableRef.current?.contains(relatedTarget)) {
      return;
    }
    setTimeout(() => {
      setResizing(null);
      setIsFocused(false);
    }, 150);
  }, []);

  // Column resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent, colIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    const colCount = data.rows[0]?.length || 1;
    const currentWidths = data.columnWidths || Array(colCount).fill(100 / colCount);
    
    setResizing({
      colIndex,
      startX: e.clientX,
      startWidths: [...currentWidths]
    });
  }, [data.rows, data.columnWidths]);

  const handleResizeMove = useCallback((e: React.MouseEvent) => {
    if (!resizing || !tableRef.current) return;
    
    const tableWidth = tableRef.current.offsetWidth;
    const deltaX = e.clientX - resizing.startX;
    const deltaPercent = (deltaX / tableWidth) * 100;
    
    const newWidths = [...resizing.startWidths];
    const minWidth = 10; // Minimum 10% width
    
    // Adjust the resized column and the next column
    const newLeftWidth = Math.max(minWidth, resizing.startWidths[resizing.colIndex] + deltaPercent);
    const newRightWidth = Math.max(minWidth, resizing.startWidths[resizing.colIndex + 1] - deltaPercent);
    
    // Only apply if both columns are above minimum
    if (newLeftWidth >= minWidth && newRightWidth >= minWidth) {
      newWidths[resizing.colIndex] = newLeftWidth;
      newWidths[resizing.colIndex + 1] = newRightWidth;
      onChange({ ...data, columnWidths: newWidths });
    }
  }, [resizing, data, onChange]);

  const handleResizeEnd = useCallback(() => {
    setResizing(null);
  }, []);

  // Get column widths (default to equal distribution)
  const getColumnWidths = () => {
    const colCount = data.rows[0]?.length || 1;
    return data.columnWidths || Array(colCount).fill(100 / colCount);
  };

  const columnWidths = getColumnWidths();

  return (
    <div 
      ref={tableRef}
      className={`table-block ${isFocused ? 'focused' : ''} ${resizing ? 'resizing' : ''}`}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseMove={resizing ? handleResizeMove : undefined}
      onMouseUp={resizing ? handleResizeEnd : undefined}
      onMouseLeave={resizing ? handleResizeEnd : undefined}
    >
      {/* Table Toolbar */}
      {isEditing && isFocused && (
        <div 
          className="table-toolbar"
          onMouseDown={(e) => e.preventDefault()}
        >
          <button className="toolbar-btn" onClick={addRow} title="Add row">
            <span className="icon">+</span>
            <span>Row</span>
          </button>
          <button className="toolbar-btn" onClick={addColumn} title="Add column">
            <span className="icon">+</span>
            <span>Column</span>
          </button>
          <div className="toolbar-divider" />
          <button 
            className="toolbar-btn" 
            onClick={deleteRow} 
            disabled={data.rows.length <= 1}
            title="Delete last row"
          >
            <span className="icon">−</span>
            <span>Row</span>
          </button>
          <button 
            className="toolbar-btn" 
            onClick={deleteColumn}
            disabled={data.rows[0]?.length <= 1}
            title="Delete last column"
          >
            <span className="icon">−</span>
            <span>Column</span>
          </button>
          <div className="toolbar-divider" />
          <button
            className={`toolbar-btn ${data.hasHeaderRow ? 'active' : ''}`}
            onClick={toggleHeaderRow}
            title="Toggle header row"
          >
            <span className="icon">H</span>
            <span>Header</span>
          </button>
        </div>
      )}

      <table className="editable-table" style={{ tableLayout: 'fixed' }}>
        <colgroup>
          {columnWidths.map((width, i) => (
            <col key={i} style={{ width: `${width}%` }} />
          ))}
        </colgroup>
        <tbody>
          {data.rows.map((row, rowIndex) => (
            <tr key={rowIndex} className={rowIndex === 0 && data.hasHeaderRow ? 'header-row' : ''}>
              {row.map((cell, colIndex) => {
                const CellTag = rowIndex === 0 && data.hasHeaderRow ? 'th' : 'td';
                return (
                  <CellTag key={colIndex} className="table-cell">
                    <LiveText
                      className="cell-content"
                      value={cell.content}
                      editable={isEditing}
                      restoreKey={`${block.id}:${rowIndex}:${colIndex}`}
                      onCommit={(content) => updateCell(rowIndex, colIndex, content)}
                      data-placeholder="..."
                    />
                    {/* Column resize handle */}
                    {isEditing && colIndex < row.length - 1 && (
                      <div 
                        className="column-resize-handle"
                        onMouseDown={(e) => handleResizeStart(e, colIndex)}
                      />
                    )}
                  </CellTag>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TableBlock;
